import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRBAC } from '../../context/RBACContext'
import { useI18n } from '../../i18n/I18nContext'
import { adminService } from '../../services/admin'
import { apiErrorMessage } from '../../services/access'
import { MapperLeafletMap } from './mapper/MapperLeafletMap'
import { locationDisplayAddress } from '../../services/locationAddress'
import type { ActivePunchGroup, ActivePunchRow, MapperAccess, MapperLocation, MapperOrganization, SearchTarget } from './mapper/types'

type SearchResult = {
  place_id: number
  display_name: string
  lat: string
  lon: string
}

const emptyDraft: MapperLocation = {
  company: '',
  name: '',
  type: 'circle',
  circle: null,
  polygon: null,
  requireShift: true,
  activeTracking: false,
  trackingEnabled: false,
  ownerOrgId: '',
}

function hasCircleShape(draft: MapperLocation) {
  return draft.type === 'circle'
    && Number.isFinite(draft.circle?.lat)
    && Number.isFinite(draft.circle?.lon)
    && Number(draft.circle?.radius) > 0
}

function hasPolygonShape(draft: MapperLocation) {
  return draft.type === 'polygon' && Array.isArray(draft.polygon) && draft.polygon.length >= 3
}

function locationLabel(location: MapperLocation, untitled = 'Untitled location') {
  const address = locationDisplayAddress(location)
  const owner = location.ownerOrgName || location.owner_org_name || location.company
  const name = location.name || location.label || untitled
  return [owner, name, address].filter(Boolean).join(' - ')
}

function numberInputValue(value?: number) {
  return Number.isFinite(value) ? value : ''
}

function groupLocationKey(group: ActivePunchGroup) {
  return group.key || [group.company, group.name].filter(Boolean).join('::')
}

export const Mapper: React.FC<{
  initialLocationId?: string
  initialOwnerOrgId?: string
  lockedLocation?: boolean
  readOnly?: boolean
  compact?: boolean
  onCreateJobFromLocation?: (location: MapperLocation) => void
  canCreateJobs?: boolean
  onSaved?: () => void
}> = ({ initialLocationId = '', initialOwnerOrgId = '', lockedLocation = false, readOnly = false, compact = false, onCreateJobFromLocation, canCreateJobs = Boolean(onCreateJobFromLocation), onSaved }) => {
  const { hasFeature } = useRBAC()
  const { t, formatDateTime, formatNumber } = useI18n()
  const [locations, setLocations] = useState<MapperLocation[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [draft, setDraft] = useState<MapperLocation>(emptyDraft)
  const [draftShapeReady, setDraftShapeReady] = useState(false)
  // When on, saving the fence also resolves the location's worksite coordinates
  // from the pin (a confirmed manual resolution), so onsite jobs can publish.
  const [resolveFromPin, setResolveFromPin] = useState(true)
  const [activePunches, setActivePunches] = useState<Record<string, ActivePunchGroup>>({})
  const [punchCursor, setPunchCursor] = useState('')
  const [access, setAccess] = useState<MapperAccess | null>(null)
  const [organizations, setOrganizations] = useState<MapperOrganization[]>([])
  const [status, setStatus] = useState(t('Loading access context...'))
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [importText, setImportText] = useState('')
  const [importFileName, setImportFileName] = useState('')
  const [importCompany, setImportCompany] = useState('')
  const [importRadius, setImportRadius] = useState('150')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchTarget, setSearchTarget] = useState<SearchTarget | null>(null)
  const [searching, setSearching] = useState(false)
  const [punchStatus, setPunchStatus] = useState(t('Punch monitor is ready.'))
  const selectedIdRef = useRef('')
  const punchCursorRef = useRef('')
  const punchBackoffRef = useRef(60000)

  const canEditLocations = !readOnly && (access?.features?.editLocations ?? hasFeature('editLocations'))
  const canDeleteLocations = access?.features?.deleteLocations ?? hasFeature('deleteLocations')
  const canImportLocations = access?.features?.importLocations ?? hasFeature('importLocations')

  const selectedLocation = useMemo(
    () => locations.find((location) => location.id === selectedId),
    [locations, selectedId]
  )
  const activeOrganizations = useMemo(() => organizations.filter((org) => org.status !== 'archived'), [organizations])
  const selectedOwnerOrgId = draft.ownerOrgId || draft.owner_org_id || initialOwnerOrgId || ''
  const selectedOwnerOrg = activeOrganizations.find((org) => org.id === selectedOwnerOrgId)
  const groupedOrganizations = useMemo(() => ({
    Companies: activeOrganizations.filter((org) => org.org_type === 'company'),
    Agencies: activeOrganizations.filter((org) => org.org_type === 'agency'),
    Other: activeOrganizations.filter((org) => org.org_type !== 'company' && org.org_type !== 'agency'),
  }), [activeOrganizations])
  const locationsForSelectedOwner = useMemo(() => {
    if (!selectedOwnerOrgId) return locations
    return locations.filter((location) => (location.ownerOrgId || location.owner_org_id || location.companyOrgId || location.company_org_id) === selectedOwnerOrgId)
  }, [locations, selectedOwnerOrgId])
  const hasValidManualCircle = hasCircleShape(draft)

  const activePunchRows = useMemo<ActivePunchRow[]>(() => Object.values(activePunches).flatMap((group) => (
    group.people || []
  ).map((person) => ({
    ...person,
    locationKey: groupLocationKey(group),
    location: group.label || [group.company, group.name].filter(Boolean).join(' - ') || group.key,
  }))), [activePunches])

  const activeCountByKey = useMemo(() => {
    const counts = new Map<string, number>()
    Object.values(activePunches).forEach((group) => {
      counts.set(groupLocationKey(group), group.people?.length || 0)
    })
    return counts
  }, [activePunches])

  const canSave = canEditLocations
    && Boolean(draft.ownerOrgId || draft.owner_org_id || (activeOrganizations.length === 0 && draft.company?.trim()))
    && Boolean(draft.name?.trim())
    && (draftShapeReady || hasValidManualCircle)
    && (hasCircleShape(draft) || hasPolygonShape(draft))
    && !loading

  const loadMapper = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminService.getFenceMapperBootstrap(initialOwnerOrgId || undefined)
      const nextLocations = (response.data.locations || []) as MapperLocation[]
      setLocations(nextLocations)
      setOrganizations(response.data.organizations || [])
      setActivePunches(response.data.activePunches || {})
      setPunchCursor(response.data.punchCursor || '')
      punchCursorRef.current = response.data.punchCursor || ''
      setAccess(response.data.adminAccess || null)
      setStatus(nextLocations.length ? t('Fence map loaded.') : t('No saved fences yet. Draw one on the map to start.'))
      const currentSelectedId = selectedIdRef.current || initialLocationId
      if (initialLocationId && nextLocations.some((location) => location.id === initialLocationId)) {
        setSelectedId(initialLocationId)
      }
      if (currentSelectedId && !nextLocations.some((location) => location.id === currentSelectedId)) {
        setSelectedId('')
      }
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to load mapper data right now.')))
    } finally {
      setLoading(false)
    }
  }, [initialLocationId, initialOwnerOrgId, t])

  const refreshActivePunches = useCallback(async (options: { silent?: boolean } = {}) => {
    if (!options.silent) {
      setLoading(true)
      setError('')
    }
    try {
      const response = await adminService.getActivePunchPush(punchCursorRef.current)
      setActivePunches(response.data.activePunches || {})
      setPunchCursor(response.data.cursor || '')
      punchCursorRef.current = response.data.cursor || ''
      punchBackoffRef.current = response.data.events?.length ? 2000 : 60000
      setPunchStatus(response.data.events?.length ? t('Active punch state updated.') : t('Active punch state is current.'))
    } catch (err) {
      punchBackoffRef.current = Math.min(Math.max(punchBackoffRef.current * 2, 4000), 60000)
      if (!options.silent) {
        setError(apiErrorMessage(err, t('Unable to refresh active punches right now.')))
      }
    } finally {
      if (!options.silent) setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadMapper()
  }, [loadMapper])

  useEffect(() => {
    punchCursorRef.current = punchCursor
  }, [punchCursor])

  useEffect(() => {
    selectedIdRef.current = selectedId
  }, [selectedId])

  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (cancelled) return
        await refreshActivePunches({ silent: true })
        if (!cancelled) schedule()
      }, punchBackoffRef.current)
    }
    schedule()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [refreshActivePunches])

  useEffect(() => {
    if (!selectedLocation) return
    const normalized: MapperLocation = {
      ...emptyDraft,
      ...selectedLocation,
      company: selectedLocation.company || '',
      name: selectedLocation.name || '',
      address: locationDisplayAddress(selectedLocation),
      type: selectedLocation.type === 'polygon' ? 'polygon' : 'circle',
      circle: selectedLocation.circle || null,
      polygon: selectedLocation.polygon || null,
      activeTracking: Boolean(selectedLocation.activeTracking || selectedLocation.trackingEnabled),
      trackingEnabled: Boolean(selectedLocation.activeTracking || selectedLocation.trackingEnabled),
      ownerOrgId: selectedLocation.ownerOrgId || selectedLocation.owner_org_id || '',
    }
    setDraft(normalized)
    setDraftShapeReady(hasCircleShape(normalized) || hasPolygonShape(normalized))
    // Default the pin-resolve toggle on unless the worksite coordinates are
    // already resolved, so an unresolved location gets fixed without extra clicks
    // while an already-resolved one is not silently overwritten.
    const alreadyResolved = (selectedLocation.addressStatus === 'resolved' || selectedLocation.addressStatus === 'verified')
      && Number.isFinite(selectedLocation.latitude) && Number.isFinite(selectedLocation.longitude)
    setResolveFromPin(!alreadyResolved)
    setStatus(t('Fence selected. Use the map edit handles or draw a replacement shape.'))
  }, [selectedLocation, t])

  const startNewLocation = (ownerOrgId = selectedOwnerOrgId) => {
    const owner = activeOrganizations.find((org) => org.id === ownerOrgId)
    setSelectedId('')
    setDraft({ ...emptyDraft, ownerOrgId, company: owner?.name || '' })
    setDraftShapeReady(false)
    setResolveFromPin(true)
    setStatus(owner ? t('Draw one circle or polygon for {name}.', { name: owner.name }) : t('Select a company or owner, then draw one circle or polygon.'))
    setError('')
  }

  const handleOwnerChange = (ownerOrgId: string) => {
    const owner = activeOrganizations.find((org) => org.id === ownerOrgId)
    setDraft((current) => ({ ...current, ownerOrgId, company: owner?.name || current.company || '' }))
    const selectedOwner = selectedLocation?.ownerOrgId || selectedLocation?.owner_org_id
    if (selectedOwner && selectedOwner !== ownerOrgId) setSelectedId('')
  }

  const handleExistingLocationChange = (value: string) => {
    if (value === '__new__') {
      startNewLocation(selectedOwnerOrgId)
      return
    }
    setSelectedId(value)
  }

  const handleShapeDraft = (shape: any) => {
    setDraft((current) => ({
      ...current,
      type: shape.type,
      circle: shape.type === 'circle' ? shape.circle : null,
      polygon: shape.type === 'polygon' ? shape.polygon : null,
    }))
    setDraftShapeReady(true)
    setStatus(shape.type === 'circle' ? t('Circle captured from the map.') : t('Polygon captured from the map.'))
  }

  const updateCircle = (field: 'lat' | 'lon' | 'radius', value: string) => {
    const nextValue = value.trim() ? Number(value) : Number.NaN
    setDraft((current) => ({
      ...current,
      type: 'circle',
      circle: {
        lat: current.circle?.lat ?? Number.NaN,
        lon: current.circle?.lon ?? Number.NaN,
        radius: current.circle?.radius ?? 150,
        [field]: nextValue,
      },
    }))
    setDraftShapeReady(true)
  }

  const saveLocation = async () => {
    if (!canSave) {
      if (!draft.name?.trim()) setError(t('Location name is required.'))
      else if (!draft.ownerOrgId && !draft.owner_org_id && (activeOrganizations.length > 0 || !draft.company?.trim())) setError(t('Owner organization is required.'))
      else if (draft.type === 'circle') setError(t('Circle latitude, longitude, and radius are required.'))
      else setError(t('Draw a geofence shape or enter valid circle coordinates before saving.'))
      return
    }
    setLoading(true)
    setError('')
    try {
      const payload = {
        id: draft.id,
        company: draft.company,
        name: draft.name,
        address: typeof draft.address === 'string' ? draft.address : locationDisplayAddress(draft),
        ownerOrgId: draft.ownerOrgId || draft.owner_org_id || null,
        type: draft.type === 'polygon' ? 'polygon' : 'circle',
        circle: draft.type === 'circle' ? draft.circle : undefined,
        polygon: draft.type === 'polygon' ? draft.polygon : undefined,
        requireShift: Boolean(draft.requireShift),
        resolveLocationFromPin: resolveFromPin,
        ...(draft.id ? {} : { activeTracking: false }),
      }
      if (draft.id) {
        await adminService.updateLocation(draft.id, payload)
      } else {
        const response = await adminService.saveLocationFromMapper(payload)
        if (response.data?.location?.id) setSelectedId(response.data.location.id)
      }
      setStatus(t('Fence saved to location. Live worker tracking is controlled from Location Edit.'))
      setDraft(emptyDraft)
      setDraftShapeReady(false)
      await loadMapper()
      onSaved?.()
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to save this fence. Check the shape and try again.')))
    } finally {
      setLoading(false)
    }
  }

  const archiveLocation = async (id = draft.id || '') => {
    if (!id || !canDeleteLocations) return
    const target = locations.find((location) => location.id === id)
    if (!window.confirm(t('Archive {location}?', { location: target ? locationLabel(target, t('Untitled location')) : t('this location') }))) return
    setLoading(true)
    setError('')
    try {
      await adminService.deleteLocation(id)
      if (id === draft.id) {
        setSelectedId('')
        setDraft(emptyDraft)
        setDraftShapeReady(false)
      }
      setStatus(t('Location archived.'))
      await loadMapper()
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to archive this location right now.')))
    } finally {
      setLoading(false)
    }
  }

  const onImportFile = async (file?: File) => {
    if (!file) {
      setImportFileName('')
      return
    }
    setError('')
    try {
      const text = await file.text()
      setImportText(text)
      setImportFileName(file.name)
      setStatus(t('{file} loaded for import.', { file: file.name }))
    } catch {
      setError(t('Unable to read that file.'))
    }
  }

  const importLocations = async () => {
    if (!importText.trim() || !canImportLocations) return
    setLoading(true)
    setError('')
    try {
      let payload: any = {
        content: importText,
        defaultCompany: importCompany || draft.company || '',
        defaultRadiusM: Number(importRadius) || 150,
      }
      if (!importText.trim().startsWith('<')) {
        try {
          payload = {
            ...JSON.parse(importText),
            defaultCompany: importCompany || draft.company || '',
            defaultRadiusM: Number(importRadius) || 150,
          }
        } catch {
          payload = {
            content: importText,
            defaultCompany: importCompany || draft.company || '',
            defaultRadiusM: Number(importRadius) || 150,
          }
        }
      }
      const response = await adminService.importLocationsFromMyMaps(payload)
      setStatus(response.data.skippedCount
        ? t('{imported} locations imported, {skipped} skipped.', { imported: response.data.importedCount || 0, skipped: response.data.skippedCount })
        : t('{count} locations imported.', { count: response.data.importedCount || 0 }))
      setImportText('')
      setImportFileName('')
      await loadMapper()
    } catch (err) {
      setError(apiErrorMessage(err, t('Import failed. Use a KML, GeoJSON, JSON, or supported My Maps export.')))
    } finally {
      setLoading(false)
    }
  }

  const searchAddress = async () => {
    const query = searchQuery.trim()
    if (!query) return
    setSearching(true)
    setError('')
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}`)
      if (!response.ok) throw new Error('search failed')
      const results = await response.json() as SearchResult[]
      setSearchResults(results)
      setStatus(results.length ? t('Select an address result to move the map.') : t('No address results found.'))
    } catch {
      setError(t('Address search is unavailable right now.'))
    } finally {
      setSearching(false)
    }
  }

  const selectSearchResult = (result: SearchResult) => {
    const lat = Number(result.lat)
    const lon = Number(result.lon)
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return
    setSearchTarget({ lat, lon, label: result.display_name })
    setSearchResults([])
    setStatus(t('Map moved to the selected address.'))
  }

  const selectedActiveCount = selectedLocation
    ? activeCountByKey.get(selectedLocation.locationKey || selectedLocation.key || '') || 0
    : 0

  return (
    <section className="panel workspace-panel stack mapper-workspace" data-testid="workspace-panel">
      <header className="mapper-header">
        <div>
          <h2 className={compact ? 'mapper-section-title' : 'heading-lg'}>{lockedLocation ? t('Location geofence') : t('Fence mapper')}</h2>
          <p className="text-muted">{readOnly ? t('Review this location geofence in read-only mode.') : t('Draw, import, edit, and monitor PostgreSQL-backed geofence boundaries from the map.')}</p>
        </div>
        <div className="inline-group">
          <button type="button" className="btn btn-secondary" onClick={loadMapper} disabled={loading}>{t('Refresh fences')}</button>
          <button type="button" className="btn btn-secondary" onClick={() => refreshActivePunches()} disabled={loading}>{t('Refresh punches')}</button>
          {!lockedLocation ? <button type="button" className="btn btn-primary" onClick={() => startNewLocation()} disabled={!canEditLocations}>{t('New fence')}</button> : null}
        </div>
      </header>

      <div className="mapper-workspace-cards">
        <article className="mapper-summary-card">
          <strong>{t('Draw geofences')}</strong>
          <span>{canEditLocations ? t('Circle and polygon tools are active on the map.') : t('You can review fences, but editing is disabled.')}</span>
        </article>
        <article className="mapper-summary-card">
          <strong>{t('Fence inventory')}</strong>
          <span>{t('{count} active fences loaded.', { count: formatNumber(locations.length) })}</span>
        </article>
        <article className="mapper-summary-card">
          <strong>{t('Punch monitor')}</strong>
          <span>{t('{count} workers currently active.', { count: formatNumber(activePunchRows.length) })}</span>
        </article>
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {status ? <p className="helper">{status}</p> : null}

      {!access && loading ? <p className="helper">{t('Loading access context...')}</p> : null}

      <section className="mapper-map-workspace">
        <aside className="mapper-control-panel stack">
          <div className="stack" style={{ gap: '.65rem' }}>
            <h3 className="mapper-section-title">{t('Map controls')}</h3>
            <div className="form-row">
              <label htmlFor="addressSearchInput">{t('Search address')}</label>
              <div className="mapper-search-box">
                <input
                  id="addressSearchInput"
                  className="input"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      searchAddress()
                    }
                  }}
                  placeholder={t('123 Main St')}
                  autoComplete="off"
                />
                <button type="button" className="btn btn-secondary" onClick={searchAddress} disabled={searching || !searchQuery.trim()}>
                  {t('Search')}
                </button>
              </div>
              {searchResults.length ? (
                <ul className="mapper-search-results">
                  {searchResults.map((result) => (
                    <li key={result.place_id}>
                      <button type="button" onClick={() => selectSearchResult(result)}>{result.display_name}</button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="stack" style={{ gap: '.65rem' }}>
            <h3 className="mapper-section-title">{t('Step 1: Select company / owner organization')}</h3>
            <div className="form-row">
              <label htmlFor="ownerOrgInput">{t('Company / owner')}</label>
              <select id="ownerOrgInput" className="input" value={selectedOwnerOrgId} onChange={(event) => handleOwnerChange(event.target.value)} disabled={lockedLocation || !canEditLocations || activeOrganizations.length === 0}>
                <option value="">{t('Select company / owner')}</option>
                {Object.entries(groupedOrganizations).map(([group, orgs]) => orgs.length ? (
                  <optgroup key={group} label={t(group)}>
                    {orgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
                  </optgroup>
                ) : null)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="existingLocationInput">{t('Existing location')}</label>
              <select id="existingLocationInput" className="input" value={selectedId || '__new__'} onChange={(event) => handleExistingLocationChange(event.target.value)} disabled={lockedLocation || !canEditLocations || !selectedOwnerOrgId}>
                <option value="__new__">{t('Create new location')}</option>
                {locationsForSelectedOwner.map((location) => <option key={location.id || location.locationKey} value={location.id}>{locationLabel(location, t('Untitled location'))}</option>)}
              </select>
              <span className="helper">{locationsForSelectedOwner.length ? t('Select a saved location to edit its fence, or create a new one.') : t('Create a location/fence for the selected company.')}</span>
            </div>
          </div>

          <div className="stack" style={{ gap: '.65rem' }}>
            <h3 className="mapper-section-title">{t('Step 3: Location details')}</h3>
            <div className="form-row">
              <label htmlFor="companyInput">{t('Company / Client')}</label>
              <input id="companyInput" className="input" value={selectedOwnerOrg?.name || draft.company || ''} onChange={(event) => setDraft({ ...draft, company: event.target.value })} disabled={Boolean(selectedOwnerOrg)} />
            </div>
            <div className="form-row">
              <label htmlFor="nameInput">{t('Location name')}</label>
              <input id="nameInput" className="input" value={draft.name || ''} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </div>
            <div className="form-row">
              <label htmlFor="addressInput">{t('Address / notes')}</label>
              <input id="addressInput" className="input" value={typeof draft.address === 'string' ? draft.address : locationDisplayAddress(draft)} onChange={(event) => setDraft({ ...draft, address: event.target.value })} />
            </div>
            <div className="form-row">
              <label htmlFor="requireShiftInput">{t('Punch access')}</label>
              <select id="requireShiftInput" className="input" value={draft.requireShift ? 'require' : 'allow'} onChange={(event) => setDraft({ ...draft, requireShift: event.target.value === 'require' })}>
                <option value="require">{t('Require scheduled shift upload')}</option>
                <option value="allow">{t('Allow punches without schedule')}</option>
              </select>
              <span className="helper">{t('Live worker tracking is configured from Location Edit. This mapper only saves geofence boundaries.')}</span>
            </div>

            <h3 className="mapper-section-title">{t('Step 4: Draw fence')}</h3>
            <p className="helper">{t('Draw the boundary for {location}.', { location: [selectedOwnerOrg?.name || draft.company, draft.name || t('new location')].filter(Boolean).join(' / ') })}</p>
            <div className="mapper-draw-status">
              {draftShapeReady ? (
                draft.type === 'circle' && draft.circle ? (
                  <span>{t('Circle: {lat}, {lon} - {radius} m', { lat: draft.circle.lat.toFixed(6), lon: draft.circle.lon.toFixed(6), radius: formatNumber(Math.round(draft.circle.radius)) })}</span>
                ) : (
                  <span>{t('Polygon: {count} points captured', { count: formatNumber(draft.polygon?.length || 0) })}</span>
                )
              ) : (
                <span>{t('Draw a shape on the map or enter latitude, longitude, and radius.')}</span>
              )}
            </div>

            {draft.type === 'circle' ? (
              <div className="mapper-coordinate-grid">
                <div className="form-row">
                  <label htmlFor="mapperLat">{t('Latitude')}</label>
                  <input id="mapperLat" className="input" type="number" value={numberInputValue(draft.circle?.lat)} onChange={(event) => updateCircle('lat', event.target.value)} />
                </div>
                <div className="form-row">
                  <label htmlFor="mapperLon">{t('Longitude')}</label>
                  <input id="mapperLon" className="input" type="number" value={numberInputValue(draft.circle?.lon)} onChange={(event) => updateCircle('lon', event.target.value)} />
                </div>
                <div className="form-row">
                  <label htmlFor="mapperRadius">{t('Radius')}</label>
                  <input id="mapperRadius" className="input" type="number" value={numberInputValue(draft.circle?.radius)} onChange={(event) => updateCircle('radius', event.target.value)} />
                </div>
              </div>
            ) : (
              <div className="form-row">
                <label htmlFor="mapperPolygon">{t('Polygon points')}</label>
                <textarea
                  id="mapperPolygon"
                  className="input"
                  rows={4}
                  value={JSON.stringify(draft.polygon || [])}
                  onChange={(event) => {
                    try {
                      const next = JSON.parse(event.target.value)
                      setDraft({ ...draft, type: 'polygon', polygon: next })
                      setDraftShapeReady(Array.isArray(next) && next.length >= 3)
                    } catch {
                      setDraftShapeReady(false)
                    }
                  }}
                />
              </div>
            )}

            <label className="mapper-resolve-pin" htmlFor="mapperResolveFromPin">
              <input
                id="mapperResolveFromPin"
                type="checkbox"
                checked={resolveFromPin}
                onChange={(event) => setResolveFromPin(event.target.checked)}
              />
              <span>{t('Use this pin as the worksite location (resolves the address so onsite jobs can publish).')}</span>
            </label>

            <div className="inline-group">
              <button type="button" className="btn btn-primary" onClick={saveLocation} disabled={!canSave}>{t('Save fence to location')}</button>
              <button type="button" className="btn btn-secondary" onClick={() => archiveLocation()} disabled={!draft.id || !canDeleteLocations || loading}>{t('Archive')}</button>
            </div>
          </div>

          <div className="stack" style={{ gap: '.65rem' }}>
            <h3 className="mapper-section-title">{t('Import from My Maps')}</h3>
            <p className="helper">{t('Upload KML, GeoJSON, or JSON. The text area is kept for advanced import payloads.')}</p>
            <div className="form-row">
              <label htmlFor="myMapsFileInput">{t('Export file')}</label>
              <input
                id="myMapsFileInput"
                type="file"
                accept=".kml,.geojson,.json"
                onChange={(event) => onImportFile(event.target.files?.[0])}
              />
              <span className="helper">{importFileName || t('No file selected.')}</span>
            </div>
            <div className="mapper-coordinate-grid">
              <div className="form-row">
                <label htmlFor="myMapsCompanyInput">{t('Default company')}</label>
                <input id="myMapsCompanyInput" className="input" value={importCompany} onChange={(event) => setImportCompany(event.target.value)} placeholder={draft.company || t('Optional')} />
              </div>
              <div className="form-row">
                <label htmlFor="myMapsRadiusInput">{t('Default radius')}</label>
                <input id="myMapsRadiusInput" className="input" type="number" min={1} value={importRadius} onChange={(event) => setImportRadius(event.target.value)} />
              </div>
            </div>
            <textarea className="input mapper-import-textarea" value={importText} onChange={(event) => setImportText(event.target.value)} placeholder={t('<kml>...</kml> or GeoJSON/JSON payload')} />
            <button type="button" className="btn btn-secondary" onClick={importLocations} disabled={!importText.trim() || !canImportLocations || loading}>
              {t('Import locations')}
            </button>
          </div>
        </aside>

        <div className="mapper-map-panel">
          <MapperLeafletMap
            locations={locations}
            selectedId={selectedId}
            draft={draft}
            activePunches={activePunches}
            searchTarget={searchTarget}
            canEdit={canEditLocations}
            canDelete={canDeleteLocations}
            onSelectLocation={setSelectedId}
            onCreateJobFromLocation={onCreateJobFromLocation}
            canCreateJobs={canCreateJobs}
            onShapeDraft={handleShapeDraft}
            onArchiveLocation={archiveLocation}
          />
        </div>
      </section>

      <section className="mapper-lower-grid">
        <article className="panel workspace-panel stack mapper-inventory-panel">
          <div className="mapper-section-heading">
            <div>
              <h3 className="mapper-section-title">{t('Fence inventory')}</h3>
              <p className="helper">{t('Click a row to highlight it on the map and edit its shape.')}</p>
            </div>
            <span className="mapper-badge">{formatNumber(locationsForSelectedOwner.length)}</span>
          </div>
          <div className="mapper-inventory-list">
            {locationsForSelectedOwner.length === 0 ? (
              <p className="helper">{t('No locations configured.')}</p>
            ) : locationsForSelectedOwner.map((location) => {
              const activeCount = activeCountByKey.get(location.locationKey || location.key || '') || 0
              return (
                <button
                  type="button"
                  key={location.id || location.key}
                  className={`mapper-inventory-row ${selectedId === location.id ? 'mapper-inventory-row--active' : ''}`}
                  onClick={() => setSelectedId(location.id || '')}
                >
                  <span>
                    <strong>{locationLabel(location, t('Untitled location'))}</strong>
                    <small>{location.type === 'polygon' ? t('Polygon') : t('Circle')} · {location.requireShift ? t('shift required') : t('unscheduled allowed')}</small>
                  </span>
                  <span className={activeCount ? 'mapper-badge mapper-badge--live' : 'mapper-badge'}>{formatNumber(activeCount)}</span>
                </button>
              )
            })}
          </div>
        </article>

        <article className="panel workspace-panel stack mapper-punch-panel">
          <div className="mapper-section-heading">
            <div>
              <h3 className="mapper-section-title">{t('Punch monitor')}</h3>
              <p className="helper">{punchStatus}</p>
            </div>
            <span className="mapper-badge mapper-badge--live">{t('Auto')}</span>
          </div>
          <div className="panel mapper-punch-table-wrap">
            <table className="workspace-table">
              <thead><tr><th>{t('Worker')}</th><th>{t('Phone')}</th><th>{t('Status')}</th><th>{t('Location')}</th><th>{t('Last update')}</th></tr></thead>
              <tbody>
                {activePunchRows.length === 0 ? (
                  <tr><td colSpan={5} className="helper">{t('No active punches are currently reported.')}</td></tr>
                ) : activePunchRows.map((row) => (
                  <tr key={`${row.phone || row.workerName}-${row.timestamp}`}>
                    <td>{row.workerName || t('Unknown worker')}</td>
                    <td>{row.phone || '-'}</td>
                    <td>{row.action || '-'}</td>
                    <td>{row.location}</td>
                    <td>{row.timestamp ? formatDateTime(row.timestamp) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {selectedLocation ? (
        <p className="helper">{t('Selected: {location} - {count} active punches', { location: locationLabel(selectedLocation, t('Untitled location')), count: formatNumber(selectedActiveCount) })}</p>
      ) : null}
    </section>
  )
}
